import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_BASE_URL, FINANCE_ADDRESS, FINANCE_NAME } from '../../config';
import { getTodayYYYYMMDD } from '../../utils/dateUtils';
import './DailyCashbook.css';

const DailyCashbook = () => {
  const [dateFrom, setDateFrom] = useState(getTodayYYYYMMDD());
  const [dateTo, setDateTo] = useState(getTodayYYYYMMDD());

  const [cashbook, setCashbook] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const formatCurrency = (value) => {
    const amount = Number(value || 0);
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatPrintAmount = (value) =>
    new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value || 0));

  const formatDateDDMMYYYY = (iso) => {
    if (!iso || typeof iso !== 'string') return '';
    const [y, m, d] = iso.slice(0, 10).split('-');
    if (!y || !m || !d) return iso;
    return `${d}/${m}/${y}`;
  };

  const escapeHtml = (s) =>
    String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const fetchAll = useCallback(async () => {
    if (dateFrom && dateTo && new Date(dateFrom) > new Date(dateTo)) {
      setError('From date cannot be greater than To date.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const qs = `date_from=${encodeURIComponent(dateFrom)}&date_to=${encodeURIComponent(dateTo)}`;

      const cashRes = await fetch(`${API_BASE_URL}/api/partner/daily-cashbook?${qs}`);

      if (!cashRes.ok) {
        const payload = await cashRes.json().catch(() => ({}));
        throw new Error(payload.error || payload.message || 'Failed to fetch daily cashbook.');
      }

      const cashData = await cashRes.json();

      setCashbook(cashData);
    } catch (err) {
      console.error('Daily cashbook fetch failed:', err);
      setError(err.message || 'Unexpected error occurred while loading daily cashbook.');
      setCashbook(null);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  /**
   * Receipt & payment rows for the selected date range.
   * The credit/debit sides follow Secondary Balance Enquiry, while expenditure
   * heads are shown from expense_manager.headerName.
   */
  const receiptPaymentRows = useMemo(() => {
    if (!cashbook) return [];

    const rows = [];
    const amountOf = (entry) => Number(entry?.amount || 0);
    const findEntry = (entries, key) => entries?.find((e) => e.key === key) || null;
    const addRow = (headsName, credit = 0, debit = 0) => {
      const creditAmount = Number(credit || 0);
      const debitAmount = Number(debit || 0);
      if (creditAmount > 0 || debitAmount > 0) {
        rows.push({ headsName, credit: creditAmount, debit: debitAmount });
      }
    };
    const loanBucket = (t) => {
      const ty = String(t?.type || '').trim().toUpperCase();
      if (ty === 'CD') return 'CD';
      if (ty === 'TBD') return 'TBD';
      return 'GL';
    };

    const loanTx = findEntry(cashbook.debitEntries, 'new_loan_applications')?.transactions || [];
    const loanSums = { GL: 0, CD: 0, TBD: 0 };
    loanTx.forEach((t) => {
      const k = loanBucket(t);
      loanSums[k] += Number(t.loan_amount || 0);
    });

    const emiTx = findEntry(cashbook.creditEntries, 'emi')?.transactions || [];
    const emiSums = { GL: 0, CD: 0, TBD: 0 };
    emiTx.forEach((t) => {
      const k = loanBucket(t);
      emiSums[k] += Number(t.amount || 0);
    });

    const odTx = findEntry(cashbook.creditEntries, 'overdue')?.transactions || [];
    const odSums = { GL: 0, CD: 0, TBD: 0 };
    odTx.forEach((t) => {
      const k = loanBucket(t);
      odSums[k] += Number(t.amount || 0);
    });

    const glIntAmt = amountOf(findEntry(cashbook.creditEntries, 'general_loan_interest'));
    const cdIntAmt = amountOf(findEntry(cashbook.creditEntries, 'cd_loan_interest'));
    const tbdIntAmt = amountOf(findEntry(cashbook.creditEntries, 'tbd_loan_interest'));

    addRow('General Loan A/C', emiSums.GL, loanSums.GL);
    addRow('CD Loan A/C', emiSums.CD, loanSums.CD);
    addRow('TBD Loan A/C', emiSums.TBD, loanSums.TBD);

    const expTx = findEntry(cashbook.debitEntries, 'expenses')?.transactions || [];
    const byHeader = new Map();
    expTx.forEach((t) => {
      const name = String(t.headerName ?? '').trim() || 'Expense';
      byHeader.set(name, (byHeader.get(name) || 0) + Number(t.amount || 0));
    });
    Array.from(byHeader.entries())
      .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
      .forEach(([headsName, debit]) => {
        if (debit > 0) rows.push({ headsName, credit: 0, debit });
      });

    addRow('GLInterest A/C', glIntAmt, 0);
    addRow('Cdinterest A/C', cdIntAmt, 0);
    addRow('TBD Interest A/C', tbdIntAmt, 0);
    addRow('GL Overdue A/C', odSums.GL, 0);
    addRow('CD Overdue A/C', odSums.CD, 0);
    addRow('TBD Overdue A/C', odSums.TBD, 0);
    addRow('Lucky Draw A/C', 0, amountOf(findEntry(cashbook.debitEntries, 'lucky_draw')));
    addRow('Pigmy A/C', amountOf(findEntry(cashbook.creditEntries, 'pigmy_credit')), amountOf(findEntry(cashbook.debitEntries, 'pigmy_debit')));
    addRow('Total amount collected from the FD', amountOf(findEntry(cashbook.creditEntries, 'fd_collections')), 0);

    const partnerDebEntry = findEntry(cashbook.debitEntries, 'partner_debits');
    const debitHeads = partnerDebEntry?.heads;
    if (Array.isArray(debitHeads) && debitHeads.length > 0) {
      debitHeads.forEach((h) => {
        const amt = Number(h.amount || 0);
        if (amt > 0) {
          const label = String(h.name ?? '').trim() || 'Partner';
          rows.push({ headsName: `Partner (${label})`, credit: 0, debit: amt });
        }
      });
    } else if (Number(partnerDebEntry?.amount) > 0) {
      rows.push({ headsName: 'Partner Transactions', credit: 0, debit: Number(partnerDebEntry.amount) });
    }

    const partnerCredEntry = findEntry(cashbook.creditEntries, 'partner_credits');
    const creditHeads = partnerCredEntry?.heads;
    if (Array.isArray(creditHeads) && creditHeads.length > 0) {
      creditHeads.forEach((h) => {
        const amt = Number(h.amount || 0);
        if (amt > 0) {
          const label = String(h.name ?? '').trim() || 'Partner';
          rows.push({ headsName: `Partner (${label})`, credit: amt, debit: 0 });
        }
      });
    } else if (Number(partnerCredEntry?.amount) > 0) {
      rows.push({ headsName: 'Partner Transactions', credit: Number(partnerCredEntry.amount), debit: 0 });
    }

    addRow('Document Charges A/C', amountOf(findEntry(cashbook.creditEntries, 'document_charges')), 0);

    return rows.map((r, i) => ({ ...r, slNo: i + 1 }));
  }, [cashbook]);

  const columnTotals = useMemo(() => {
    let credit = 0;
    let debit = 0;
    receiptPaymentRows.forEach((r) => {
      credit += Number(r.credit || 0);
      debit += Number(r.debit || 0);
    });
    return { credit, debit };
  }, [receiptPaymentRows]);

  const handlePrintReceiptPayment = useCallback(() => {
    const from = cashbook?.range?.date_from || dateFrom;
    const to = cashbook?.range?.date_to || dateTo;
    const fromDisp = formatDateDDMMYYYY(from);
    const toDisp = formatDateDDMMYYYY(to);

    const rowsHtml =
      receiptPaymentRows.length === 0
        ? `<tr><td colspan="4" style="text-align:center;padding:12px;">No transactions for this period.</td></tr>`
        : receiptPaymentRows
            .map(
              (row) => `
          <tr>
            <td style="text-align:center;">${row.slNo}</td>
            <td>${escapeHtml(row.headsName)}</td>
            <td style="text-align:right;">${formatPrintAmount(row.credit)}</td>
            <td style="text-align:right;">${formatPrintAmount(row.debit)}</td>
          </tr>`
            )
            .join('');

    const totalCredit = formatPrintAmount(columnTotals.credit);
    const totalDebit = formatPrintAmount(columnTotals.debit);

    const addressLine = FINANCE_ADDRESS?.trim()
      ? `<div class="address">${escapeHtml(FINANCE_ADDRESS.trim())}</div>`
      : '';

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Receipt and payment</title>
  <style>
    @page { size: A4; margin: 14mm; }
    body {
      font-family: 'Times New Roman', Times, serif;
      color: #000;
      font-size: 13px;
      margin: 0;
      padding: 0;
    }
    .wrap { max-width: 800px; margin: 0 auto; }
    .company {
      text-align: center;
      font-weight: bold;
      font-size: 15px;
      text-transform: uppercase;
      letter-spacing: 0.02em;
      margin-bottom: 4px;
    }
    .address {
      text-align: center;
      font-size: 12px;
      font-weight: normal;
      margin-bottom: 10px;
    }
    .dates {
      text-align: center;
      margin: 10px 0 14px;
      font-size: 13px;
    }
    .dates strong { font-weight: bold; }
    .report-title {
      text-align: center;
      color: #8b0000;
      font-weight: bold;
      font-size: 18px;
      margin: 0 0 14px;
    }
    table.report {
      width: 100%;
      border-collapse: collapse;
      border: 2px solid #000;
    }
    table.report th,
    table.report td {
      border: 1px solid #000;
      padding: 6px 8px;
      vertical-align: middle;
    }
    table.report thead th {
      font-weight: bold;
      background: #fff;
    }
    table.report .col-sl { width: 48px; text-align: center; }
    table.report .num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
    thead { display: table-header-group; }
    tfoot { display: table-footer-group; }
    tr { break-inside: avoid; page-break-inside: avoid; }
    table.report tfoot td {
      font-weight: bold;
    }
    .end {
      text-align: center;
      margin-top: 20px;
      font-size: 12px;
      letter-spacing: 0.06em;
    }
  </style>
</head>
<body>
  <div class="wrap">
    <div class="company">${escapeHtml(FINANCE_NAME)}</div>
    ${addressLine}
    <div class="dates">FromDate: <strong>${escapeHtml(fromDisp)}</strong> &nbsp; ToDate: <strong>${escapeHtml(toDisp)}</strong></div>
    <div class="report-title">Receipt and payment</div>
    <table class="report">
      <thead>
        <tr>
          <th class="col-sl">Sl No</th>
          <th>HeadsName</th>
          <th class="num">Credit</th>
          <th class="num">Debit</th>
        </tr>
      </thead>
      <tbody>
        ${rowsHtml}
      </tbody>
      <tfoot>
        <tr>
          <td colspan="2" style="font-weight:bold;">Total</td>
          <td class="num">${totalCredit}</td>
          <td class="num">${totalDebit}</td>
        </tr>
      </tfoot>
    </table>
    <div class="end">*** END OF STATEMENT ***</div>
  </div>
</body>
</html>`;

    const w = window.open('', '_blank', 'height=900,width=900');
    if (!w) {
      alert('Please allow pop-ups to print this report.');
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => {
      try {
        w.print();
        w.close();
      } catch {
        /* ignore */
      }
    }, 300);
  }, [cashbook, columnTotals, dateFrom, dateTo, receiptPaymentRows]);

  return (
    <div className="daily-cashbook-page">
      <div className="daily-cashbook-card">
        <div className="daily-cashbook-header">
          <div>
            <h2>Receipt and payment</h2>
            <p className="subtitle">Credit and debit for the selected date range.</p>
          </div>
          <button
            type="button"
            className="btn print-header-btn"
            onClick={handlePrintReceiptPayment}
            disabled={loading || !cashbook}
            title="Opens a print preview with company header and table"
          >
            Print
          </button>
        </div>

        <div className="filters">
          <div className="filter-group">
            <label>From Date</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div className="filter-group">
            <label>To Date</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          <div className="filter-actions">
            <button className="btn primary" onClick={fetchAll} disabled={loading}>
              {loading ? 'Loading…' : 'Apply'}
            </button>
            <button
              className="btn"
              onClick={() => {
                const today = getTodayYYYYMMDD();
                setDateFrom(today);
                setDateTo(today);
              }}
              disabled={loading}
            >
              Today
            </button>
          </div>
        </div>

        {error ? <div className="notice error">{error}</div> : null}

        <div className="cashbook-table-container">
          <table className="cashbook-table receipt-payment-table">
            <thead>
              <tr>
                <th className="col-sl">Sl No</th>
                <th>Heads name</th>
                <th className="right col-money credit-heading">Credit</th>
                <th className="right col-money debit-heading">Debit</th>
              </tr>
            </thead>
            <tbody>
              {receiptPaymentRows.length === 0 ? (
                <tr>
                  <td colSpan="4" className="no-data">No transactions found for the selected date range</td>
                </tr>
              ) : (
                receiptPaymentRows.map((row) => (
                  <tr key={row.slNo}>
                    <td className="col-sl">{row.slNo}</td>
                    <td>{row.headsName}</td>
                    <td className={`right col-money ${row.credit > 0 ? 'credit-value' : 'muted-money'}`}>
                      {row.credit > 0 ? formatCurrency(row.credit) : formatCurrency(0)}
                    </td>
                    <td className={`right col-money ${row.debit > 0 ? 'debit-value' : 'muted-money'}`}>
                      {row.debit > 0 ? formatCurrency(row.debit) : formatCurrency(0)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {receiptPaymentRows.length > 0 ? (
              <tfoot>
                <tr className="receipt-payment-totals">
                  <td colSpan="2" className="total-label">
                    Total
                  </td>
                  <td className={`right col-money credit-value`}>{formatCurrency(columnTotals.credit)}</td>
                  <td className={`right col-money debit-value`}>{formatCurrency(columnTotals.debit)}</td>
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>

        {cashbook?.range ? (
          <div className="range-note">
            Showing daily cashbook between <strong>{cashbook.range.date_from}</strong> and <strong>{cashbook.range.date_to}</strong>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default DailyCashbook;


